// src/components/document-sets/DocumentSetPill.js
import React from 'react';
import { X, LockKeyhole } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';

/**
 * Individual document set pill/tag with drag support
 * Shows colored badge with name and optional remove button or lock icon
 */
const DocumentSetPill = ({
  documentSet,
  onRemove,
  showRemove = true,
  isDraggable = false,
  isReadOnly = false,
  isArchived = false, // Archived sets show lock icon instead of X
  className = '', // Additional className prop
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: documentSet.id,
    disabled: !isDraggable
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    if (!isArchived) {
      onRemove?.(documentSet.id);
    }
  };

  const getBorderColor = () => {
    // Use border color from documentSetColors.js, fallback to bg if not defined
    return documentSet.color.border || documentSet.color.bg;
  };

  return (
    <div
      ref={setNodeRef}
      className={`
        relative inline-flex items-center rounded-full text-app-2xs font-medium max-w-full min-h-[22px] border
        ${isDragging ? 'cursor-grabbing' : isDraggable ? 'cursor-grab' : ''}
        ${isReadOnly ? 'cursor-default' : ''}
        group
        ${className}
      `}
      style={{
        ...style,
        backgroundColor: documentSet.color.bg,
        color: documentSet.color.text,
        borderColor: getBorderColor(),
      }}
    >
      {/* Drag handle - only show if draggable */}
      {isDraggable && !isReadOnly && (
        <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing flex-shrink-0 pl-2 py-1">
          <GripVertical className="w-3 h-3 opacity-40 hover:opacity-70" />
        </div>
      )}

      {/* Tag name - centered with symmetric padding that slides left on hover */}
      <span className={`
        select-none truncate min-w-0 transition-all duration-app-normal py-1
        ${(showRemove && !isReadOnly) || isArchived
          ? 'pl-3 pr-3 group-hover:pl-[6px] group-hover:pr-[18px]'
          : 'px-3'
        }
      `}>
        {documentSet.name}
      </span>

      {/* Remove button or Lock icon - fades in on hover, overlays the right padding */}
      {((showRemove && !isReadOnly) || isArchived) && (
        <div className="absolute right-1 flex items-center justify-center py-1">
          {isArchived ? (
            // Show lock icon if set is archived - appears on pill hover at 70% opacity, no hover change on icon
            <div
              className="opacity-0 group-hover:opacity-70 transition-opacity duration-app-normal"
            >
              <LockKeyhole
                className="w-[10px] h-[10px] mr-[1px] mt-[-1px]"
                style={{ color: documentSet.color.text }}
              />
            </div>
          ) : (
            // Show remove button if not archived - appears on pill hover at 70%, then 100% when button hovered
            <button
              onClick={handleRemove}
              className="opacity-0 group-hover:opacity-70 hover:!opacity-100 transition-opacity duration-app-normal"
              aria-label={`Remove ${documentSet.name}`}
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default DocumentSetPill;